import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../services/api';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { queryClient } from '../App';


interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  subscription_tier: string;
  account_status?: 'waitlist' | 'active' | 'suspended';
  waitlisted_at?: string;
  activated_at?: string;
  limits: {
    max_branches: number;
    api_calls_per_day: number;
    max_chats: number;
  };
}


interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from backend
  const fetchUserProfile = async (accessToken: string) => {
    try {
      console.log('👤 Fetching user profile...');
      console.log('👤 Access token (first 20 chars):', accessToken.substring(0, 20) + '...');
      const response = await apiClient.get('/api/auth/profile', {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      console.log('✅ Profile fetched successfully:', response.data);
      setUser(response.data.user);
    } catch (error: any) {
      console.error('❌ Failed to fetch user profile:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setUser(null);
      toast.error(`Failed to load profile: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Check active session on app startup
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      console.log('Session check:', session ? 'Found' : 'Not found');
      if (session) {
        await fetchUserProfile(session.access_token);
      }
      setLoading(false);
      console.log('Loading set to false');
    }).catch((error) => {
      console.error('Session check error:', error);
      setLoading(false);
    });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('Auth state changed:', _event, session ? 'Session exists' : 'No session');
      if (session) {
        await fetchUserProfile(session.access_token);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
        await fetchUserProfile(data.session.access_token);
        toast.success('Welcome back!');
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      const message = error.message || 'Login failed';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      setLoading(true);
      console.log('Registering user with:', { email, firstName, lastName });
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName || '',
            last_name: lastName || '',
          }
        }
      });

      if (error) throw error;

      if (data.session) {
        // User is automatically signed in
        await fetchUserProfile(data.session.access_token);
        toast.success('Account created successfully!');
      } else {
        // Email confirmation required
        toast.success('Check your email to confirm your account!');
      }
    } catch (error: any) {
      console.error('Registration failed:', error);
      const message = error.message || 'Registration failed';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        }
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Google sign-in failed:', error);
      toast.error(error.message || 'Google sign-in failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 Starting logout...');

      // CRITICAL SECURITY FIX: Clear all cached data to prevent data leakage between users
      console.log('🧹 Clearing React Query cache...');
      queryClient.clear();

      // Clear any Stripe-related data from browser storage
      console.log('🧹 Clearing browser storage...');
      // Clear localStorage items that might contain sensitive data
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('stripe') || key.includes('payment'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear sessionStorage as well
      sessionStorage.clear();

      // Attempt to sign out from Supabase (may fail due to network/CORS in production)
      await supabase.auth.signOut();
      console.log('✅ Supabase signOut completed');
    } catch (error) {
      console.error('⚠️ Supabase logout error (continuing anyway):', error);
      // Continue with logout even if Supabase API request fails
      // Local session will be cleared regardless
    } finally {
      // Always clear user state immediately, regardless of API success/failure
      setUser(null);
      console.log('✅ User state cleared, logout complete');
      toast.success('Logged out successfully');
    }
  };

  const refreshUser = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetchUserProfile(session.access_token);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };


  const value = {
    user,
    loading,
    login,
    register,
    loginWithGoogle,
    logout,
    refreshUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
